import React, { DragEvent, useContext, useState } from 'react';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgCloud from '@app/pages/attachment/Cloud.tsx';
import COText from '@app/pages/COText/COText.tsx';
import SvgLoader from '@app/icons/loader/Loader.tsx';
import RowButton from '@components/RingilComponents/RowButton.tsx';
import SvgLink from '@icons/link.svg?react';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { filesFromDataTransfer } from '@components/framework/uploadpanel/fileTransfer.ts';
import useFilePaste from '@components/framework/uploadpanel/useFilePaste.ts';

interface ComponentProps {
  onClick?: () => void;
  loading?: boolean;
  showDropZone?: boolean;
  /** Files handed over by a drop or a paste. Without it the panel is click-only. */
  onFiles?: (files: File[]) => void;
  /** Off for panels that are only a button to something else and should not swallow a paste. */
  enablePaste?: boolean;
}

const UploadPanelVisual: React.FC<ComponentProps> = ({ onClick, onFiles, loading = false, showDropZone = false, enablePaste = true }) => {
  const { tr } = useTranslationLgs();
  const { showToastMessage } = useContext(ToastContext);
  const [dragOver, setDragOver] = useState(false);

  const acceptsFiles = onFiles !== undefined;

  const handleFiles = (files: File[]) => {
    if (files.length === 0) {
      // Some sources — new Outlook is the common one — hand over only metadata, so the drop reaches
      // us without a file. Saying so beats the panel looking broken.
      showToastMessage(
        tr('UploadPanelVisual.dropWithoutFile', 'The dragged content contains no file'),
        tr(
          'UploadPanelVisual.dropWithoutFileDetail',
          'The app you dragged from did not hand the file over to the browser. Save the file to disk and drag it from there, or copy it (Ctrl+C) and paste it here with Ctrl+V.',
        ),
        'warn',
        10000,
      );
      return;
    }
    onFiles(files);
  };

  useFilePaste(handleFiles, acceptsFiles && enablePaste && !loading);

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    handleFiles(filesFromDataTransfer(event.dataTransfer));
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    // Required for the drop to fire at all, and it is what turns the cursor into a copy cursor.
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    // Moving between children fires dragleave on the parent as well; ignore those.
    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
    setDragOver(false);
  };

  return (
    <>
      {!loading ? (
        <div
          // The whole panel is the drop target — the button included — so a file dropped anywhere on
          // it is taken, not just onto the dashed zone.
          className={clsx('w-full rounded-2xl border-2 border-solid transition-colors duration-200', dragOver ? 'border-content-brand bg-complementary-brandLight' : 'border-transparent')}
          onDrop={acceptsFiles ? handleDrop : undefined}
          onDragOver={acceptsFiles ? handleDragOver : undefined}
          onDragLeave={acceptsFiles ? handleDragLeave : undefined}
        >
          {showDropZone && (
            <span className={'mb-2 flex flex-row gap-4 items-center border border-dashed border-content-tertiary px-8 py-1 text-content-tertiary'}>
              <SvgCloud />
              <COText label={tr('UploadPanelVisual.pretahneteProNahrani', 'Přetáhněte pro nahrání')}></COText>
            </span>
          )}
          <RowButton
            title={tr('UploadPanelVisual.nahratSoubor', 'Nahrát soubor')}
            icon={<SvgLink width={32} height={32} className={'text-content-quaternary'} />}
            onClick={() => onClick()}
          />
        </div>
      ) : (
        <div className="w-full px-6 py-6  rounded-[8px] bg-content-quaternary ">
          <div className={'h-[32px] items-center flex flex-row justify-between'}>
            <COText className={'text-content-secondary'} label={tr('UploadPanelVisual.nahravamSoubor', 'Nahrávám soubor...')}></COText>
            <span className={'flex flex-row gap-3'}>
              <SvgLoader className={'animate-spin'} />
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadPanelVisual;